export type PersistedSliceConfig<T> = {
  key: string;
  fields: readonly (keyof T & string)[];
  // 未指定の場合は localStorage
  storage?: "local" | "session";
  // 初期値が undefined のフィールドは、指定がなければ文字列として扱う
  fieldTypes?: Partial<Record<keyof T & string, "boolean" | "number">>;
  // 指定した場合は保存時刻も保存し、この時間を過ぎた保存データは復元せずに消す
  maxAgeMs?: number;
};

const SAVED_AT_KEY = "_savedAt";

const getStorage = (storage: PersistedSliceConfig<unknown>["storage"]) =>
  storage === "session" ? window.sessionStorage : window.localStorage;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const hasSameType = (expected: unknown, value: unknown, optionalType = "string") => {
  if (expected === undefined) return typeof value === optionalType;
  if (Array.isArray(expected)) return Array.isArray(value);
  if (isRecord(expected)) return isRecord(value);
  return typeof value === typeof expected;
};

const isExpired = (maxAgeMs: number | undefined, parsed: Record<string, unknown>) => {
  if (maxAgeMs === undefined) return false;
  const savedAt = parsed[SAVED_AT_KEY];
  // 端末の時計を戻した場合などに未来の日時で期限が効かなくならないよう、未来の日時も期限切れとして扱う
  return typeof savedAt !== "number" || savedAt > Date.now() || Date.now() - savedAt > maxAgeMs;
};

// 読めない保存データ(壊れている、型が合わない、期限切れ)は undefined を返す
const parsePersistedSlice = <T extends object>(config: PersistedSliceConfig<T>, initial: T, serialized: string) => {
  try {
    const parsed: unknown = JSON.parse(serialized);
    if (!isRecord(parsed) || isExpired(config.maxAgeMs, parsed)) return undefined;
    const target = initial as Record<string, unknown>;
    // 途中のフィールドが壊れていた場合に一部だけ復元しないよう、すべて読めてから反映する。
    const restored: Record<string, unknown> = {};
    for (const key of config.fields) {
      if (!(key in parsed)) continue;
      if (!hasSameType(target[key], parsed[key], config.fieldTypes?.[key])) return undefined;
      restored[key] = parsed[key];
    }
    return restored;
  } catch {
    return undefined;
  }
};

export const readPersistedSlice = <T extends object>(config: PersistedSliceConfig<T>, initial: T): T => {
  try {
    if (typeof window === "undefined") return initial;
    const storage = getStorage(config.storage);
    const serialized = storage.getItem(config.key);
    if (!serialized) return initial;
    const restored = parsePersistedSlice(config, initial, serialized);
    // 読めない保存データは、個人情報を含む古いデータが残り続けないよう消す
    if (!restored) {
      storage.removeItem(config.key);
      return initial;
    }
    return { ...initial, ...restored };
  } catch {
    // storage が利用できない場合も起動する。
    return initial;
  }
};

export const writePersistedSlice = <T extends object>(config: PersistedSliceConfig<T>, state: T) => {
  try {
    const values = state as Record<string, unknown>;
    const entries = config.fields.filter((key) => values[key] !== undefined).map((key) => [key, values[key]]);
    if (config.maxAgeMs !== undefined) entries.push([SAVED_AT_KEY, Date.now()]);
    const serialized = JSON.stringify(Object.fromEntries(entries));
    const storage = getStorage(config.storage);
    if (storage.getItem(config.key) !== serialized) storage.setItem(config.key, serialized);
  } catch {
    // メモリ上の状態で利用を続ける。
  }
};

// persist の初回復元前は子 effect などによる初期値の保存を抑止する。
export const createPersistedSliceStorage = <T extends object>(
  config: PersistedSliceConfig<T> | undefined,
  initial: T
) => {
  let restored = false;
  return {
    getItem: (_name: string) => {
      const state = config ? readPersistedSlice(config, initial) : initial;
      restored = true;
      return { state };
    },
    setItem: (_name: string, value: { state: T }) => {
      if (config && restored) writePersistedSlice(config, value.state);
    },
    removeItem: (_name: string) => {
      if (!config) return;
      try {
        getStorage(config.storage).removeItem(config.key);
      } catch {
        // storage が利用できない場合もメモリ上の状態で継続する。
      }
    },
  };
};

type PersistableStore<T> = {
  getState: () => T;
  subscribe: (listener: (state: T, prevState: T) => void) => () => void;
};

// 別タブが保存した値を上書きしないよう、対象のフィールドが変わったときだけ書き込む。
// restored は復元直後の状態で、購読前に変わっていた分もここで保存する。
export const subscribePersistedSlice = <T extends object, S extends T>(
  store: PersistableStore<S>,
  config: PersistedSliceConfig<T>,
  restored: T
) => {
  const isChanged = (state: T, prevState: T) => config.fields.some((key) => state[key] !== prevState[key]);
  if (isChanged(store.getState(), restored)) writePersistedSlice(config, store.getState());
  return store.subscribe((state, prevState) => {
    if (isChanged(state, prevState)) writePersistedSlice(config, state);
  });
};
